import * as React from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {
    Box,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    ListItemButton,
    ListItemSecondaryAction,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    AppFormikSelect,
    AppFormikTextField,
    AppRadioBox,
} from 'shared/components/AppComponents';
import InfoIcon from '@mui/icons-material/Info';
import {
    FormikPropsFixPacketValues,
    NotExpectedMeta,
} from '../NotExpectedPage';
import { useFormikContext } from 'formik';

export default function FixList(props: ListProps): React.ReactElement {
    const {
        onViewDocument,
        filename,
        defaultValue,
        index,
        isWarned,
        isEvaluationFailed,
        name,
        categoryData,
        groupsData,
        canGroup,
        canCategorize,
        groupsSelectionName,
        unexpectedFileType,
        showCategory,
        groupName,
        isCommon,
        isSendToRework,
        isNotShowingSendToRework,
        showViewingDocument,
        onCheckedSendToRework,
    } = props;

    const theme = useTheme();
    const { values } = useFormikContext<FormikPropsFixPacketValues>();
    const NotExpectedMetaProps = React.useContext(NotExpectedMeta);
    const match = useMediaQuery(theme.breakpoints.up('md'));
    const fileContentTitle = React.useMemo(() => {
        if (groupName && canCategorize) {
            return `File Name (${groupName})`;
        }
        if (showCategory && canGroup) {
            return `File Name (${defaultValue})`;
        }
        return 'File Name';
    }, [canCategorize, canGroup, defaultValue, groupName, showCategory]);
    const handleRadioCheckbox = React.useCallback(() => {
        if (typeof onCheckedSendToRework === 'function') {
            onCheckedSendToRework(isSendToRework);
        }
    }, [isSendToRework, onCheckedSendToRework]);

    const [isChangingSelect, setOnChangeSelection] =
        React.useState<boolean>(false);
    React.useEffect(() => {
        if (isChangingSelect) {
            setOnChangeSelection(false);
        }
    }, [isChangingSelect]);
    const isCommonHighlight = React.useMemo(
        () =>
            isCommon &&
            canGroup &&
            unexpectedFileType === false &&
            groupName !== 'Loan Document' &&
            values.documents &&
            Array.isArray(values.documents) &&
            values.documents.some(
                (e, i) =>
                    i === index &&
                    Array.isArray(e.groups) &&
                    e.groups.length === 0
            ),
        [
            isCommon,
            canGroup,
            unexpectedFileType,
            groupName,
            index,
            isChangingSelect,
        ]
    );

    React.useEffect(() => {
        if (isCommonHighlight) {
            NotExpectedMetaProps?.setCommonHighlight(!isCommonHighlight);
        }
    }, [isCommonHighlight]);

    // eslint-disable-next-line consistent-return
    const isCommonTypography = React.useMemo(() => {
        if (unexpectedFileType) {
            return 'Unexpected file type is found in the packet';
        }
        if (isWarned) {
            return `Can't categorise this document. Please select
                                the category manually`;
        }
        if (isEvaluationFailed) {
            return `Document DPI and clarity evaluation failed. Please do check.`;
        }
        if (isCommonHighlight) {
            return '';
        }
    }, [isCommonHighlight, isWarned, unexpectedFileType, isEvaluationFailed]);

    return (
        <ListItem
            className={
                isWarned ||
                isEvaluationFailed ||
                unexpectedFileType ||
                isCommonHighlight
                    ? 'categorise-selection'
                    : undefined
            }
        >
            <Grid container spacing={2} className="categorization-container">
                {(isWarned || isEvaluationFailed || unexpectedFileType) && (
                    <Grid item xs={12} className="report-categorise">
                        <Stack
                            direction="row"
                            spacing={2}
                            className="stack-report-notice"
                        >
                            <WarningAmberRoundedIcon />
                            <Typography>{isCommonTypography}</Typography>
                        </Stack>
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    md={showCategory && canGroup === false ? 8 : 12}
                    lg={showCategory ? 8 : 12}
                    className="content-list"
                >
                    <Stack
                        direction={match ? 'row' : 'column'}
                        spacing={1}
                        display="flex"
                        flexGrow={1}
                    >
                        {isNotShowingSendToRework === false && (
                            <Box className="categorization-checkbox">
                                <AppRadioBox
                                    disabled={isWarned || unexpectedFileType}
                                    checked={isSendToRework}
                                    color="primary"
                                    value="testt2"
                                    onChange={handleRadioCheckbox}
                                    label="Send to Rework"
                                />
                            </Box>
                        )}

                        <Grid
                            container
                            direction="row"
                            className="content-container"
                        >
                            <Grid
                                item
                                xs={12}
                                md={8}
                                className="list-container"
                            >
                                <ListItemText
                                    primary={fileContentTitle}
                                    secondary={filename}
                                />
                            </Grid>
                            {unexpectedFileType === false &&
                            showViewingDocument === true ? (
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    className="button-container"
                                >
                                    <ListItemButton
                                        disabled={unexpectedFileType}
                                        onClick={() =>
                                            onViewDocument(index, filename)
                                        }
                                        aria-haspopup="dialog"
                                    >
                                        <ListItemText primary="View document" />
                                    </ListItemButton>
                                </Grid>
                            ) : (
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    className="button-container info-icon"
                                >
                                    {unexpectedFileType === true ? (
                                        <Tooltip
                                            placement="left-start"
                                            title="Expected File Types: PDF and Word document. Unexpected file types are not considered for Validation. Please click on save changes to Proceed. "
                                        >
                                            <IconButton>
                                                <InfoIcon fontSize="medium" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                </Grid>
                            )}
                        </Grid>
                    </Stack>
                </Grid>
                {showCategory && (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                        {canGroup === false ? (
                            <Grid
                                item
                                xs={12}
                                md={4}
                                className="categorise-selection"
                            >
                                <ListItemSecondaryAction>
                                    <FormControl fullWidth>
                                        {canCategorize ? (
                                            <AppFormikSelect
                                                testid="select-categorize"
                                                disabled={unexpectedFileType}
                                                name={name}
                                                notched={false}
                                                labelId="fix-selection-label"
                                                MenuProps={{
                                                    className:
                                                        'menu-fix-selection',
                                                }}
                                                label="Category"
                                                defaultValue={defaultValue}
                                                items={categoryData}
                                            />
                                        ) : (
                                            <AppFormikTextField
                                                testid="select-categorize"
                                                name={name}
                                                disabled
                                                notched={false}
                                                labelId="fix-selection-label"
                                                MenuProps={{
                                                    className:
                                                        'menu-fix-selection',
                                                }}
                                                defaultValue={defaultValue}
                                                items={categoryData}
                                            />
                                        )}
                                    </FormControl>
                                </ListItemSecondaryAction>
                            </Grid>
                        ) : (
                            <Grid
                                item
                                xs={12}
                                lg={4}
                                className="categorise-selection"
                            >
                                <ListItemSecondaryAction>
                                    <FormControl fullWidth>
                                        <InputLabel id="fix-selection-label">
                                            {isCommon ? '' : 'Select group'}
                                        </InputLabel>
                                        <AppFormikSelect
                                            testid="select-groups"
                                            name={groupsSelectionName}
                                            multiple
                                            notched={false}
                                            disabled={
                                                unexpectedFileType ||
                                                groupName === 'Loan Document'
                                            }
                                            placeholder={
                                                isCommon
                                                    ? 'Group Common'
                                                    : undefined
                                            }
                                            labelId="fix-selection-label"
                                            MenuProps={{
                                                className: 'menu-fix-selection',
                                            }}
                                            onChange={() => {
                                                setOnChangeSelection(true);
                                            }}
                                            items={groupsData}
                                        />
                                    </FormControl>
                                </ListItemSecondaryAction>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </ListItem>
    );
}
